import {
  DocumentOrientationEnum,
  RequiredDocumentTypeEnum
} from "@repo/core/enums/proposals";
import { Z } from "@repo/core/utils/zod.utils";

const shopkeeperDetailSchema = Z.object({
  lojista: Z.string(),
  descrLojista: Z.string()
});

const shopkeeperResponseSchema = Z.array(shopkeeperDetailSchema);

const storeDetailSchema = Z.object({
  lojista: Z.string(),
  descrLojista: Z.string(),
  loja: Z.string(),
  descrLoja: Z.string(),
  lojaLojistaPrincipal: Z.string(),
  codigoEstabelecimento: Z.string()
});

const storeDetailResponseSchema = Z.array(storeDetailSchema);

const proposalAttendantInfoSchema = Z.object({
  atendente: Z.string(),
  nome: Z.string()
});

const proposalDailySummarySchema = Z.object({
  approved: Z.number(),
  canceled: Z.number(),
  effective: Z.number(),
  rejected: Z.number(),
  others: Z.number()
});

const requiredDocumentSchema = Z.object({
  strTipoDocumento: Z.nativeEnum(RequiredDocumentTypeEnum),
  strDescricao: Z.string(),
  strNumeroPaginas: Z.number(),
  strOrientacaoPagina: Z.nativeEnum(DocumentOrientationEnum)
});

const requiredDocumentsListSchema = Z.array(requiredDocumentSchema);

export type ShopkeeperDetailSchemaType = Z.infer<typeof shopkeeperDetailSchema>;
export type ShopkeeperResponseSchemaDTO = Z.infer<
  typeof shopkeeperResponseSchema
>;

export type StoreDetailSchemaType = Z.infer<typeof storeDetailSchema>;
export type StoreDetailResponseSchemaDTO = Z.infer<
  typeof storeDetailResponseSchema
>;

export type ProposalAttendantInfoSchemaType = Z.infer<
  typeof proposalAttendantInfoSchema
>;

export type FetchAttendantsInputDTO = {
  strProduto: string;
  strBandeira: string;
  strPontoAtendimento: string;
};
export type ProposalAttendantListResponseDTO = {
  atendente: string;
  nome: string;
};

export type RequiredDocumentSchemaType = Z.infer<typeof requiredDocumentSchema>;
export type RequiredDocumentsListResponseDTO = Z.infer<
  typeof requiredDocumentsListSchema
>;
export type GetRequiredDocumentsInputDTO = {
  strProduto: string;
};

export type ProposalDailySummarySchemaType = Z.infer<
  typeof proposalDailySummarySchema
>;
export type ProposalDailySummaryResponseDTO = Z.infer<
  typeof proposalDailySummarySchema
>;

export type ProposalProductListResponseDTO = {
  abreviatura: string;
  nome: string;
  produto: string;
  saqueNaProposta: string;
  segmento: string;
  tipoPessoa: string;
  transacaoNaProposta: string;
};

export enum TIPO_CORRESPONDENCIA {
  RESIDENCIAL = "2",
  COMERCIAL = "1",
  OUTROS = "3"
}

export enum TIPO_ENVIO_FATURA {
  EMAIL = "E",
  APP = "W",
  SMS = "T",
  POST_OFFICE = "C"
}

export enum TIPO_RESIDENCIA {
  COMERCIAL = "1",
  RESIDENCIAL = "2",
  ALTERNATIVO = "3"
}

export enum TIPO_PESSOA {
  FISICA = "F",
  JURIDICA = "J"
}

export enum TIPO_OPCAO_PAGAMENTO {
  BANCO = "B"
}

export enum TIPO_FONE {
  NAO_POSSUI = "NAO POSSUI",
  CONVENCIONAL = "CONVENCIONAL",
  CELULAR = "CELULAR",
  RECADOS = "RECADOS",
  EMPRESA = "EMPRESA"
}

export enum PROPOSTA_SITUACAO {
  QUEUE_ANALYSIS_AUTOMATIC = "01",
  WAITING_FOR_ANALYSIS = "02",
  ANALYSIS_AUTOMATIC = "03",
  ANALYSIS_MANUAL = "04",
  APPROVED_AUTOMATICALLY = "05",
  APPROVED_MANUALLY = "06",
  REJECTED_AUTOMATICALLY = "07",
  REJECTED_MANUALLY = "08",
  PENDING_CORRECTION = "09",
  PENDING_ANALYSIS = "10",
  EFFECTIVE = "11",
  MANUAL_ANALYSIS_QUEUE = "12",
  ERROR_ANALYSIS = "13",
  CANCELLED = "14",
  PRE_PROPOSAL = "15",
  PRE_PROPOSAL_APPROVED = "16",
  PRE_PROPOSAL_REJECTED = "17",
  PRE_PROPOSAL_ANALYSIS = "18",
  DOCUMENT_CONFERENCE = "19",
  DATA_CONFERENCE = "20" // Draft: Can be used if attribute blnManterPendente is true on Proposta/Incluir (API)
}

export type ProposalTypeValue = "F" | "A" | string;

export enum ProposalTypeEnum {
  FIDELITY = "F",
  AMBOS = "A"
}

export interface ProposalGroupedByDateItem {
  lngProposta: number;
  strSituacao: string;
  strTipoProposta: ProposalTypeValue;
  datDataSituacao: string;
  strSituacaoDescricao: string;
  strColor: string;
  strContraste: string;
  strCor: string;
  strNome: string;
  strBusca: string;
  strProdutoDescricao: string;
  strProduto: string;
  strCliente: string;
}

export interface ProposalGroupedByDateResponseDTO {
  datDataSituacao: string;
  lstPropostas: ProposalGroupedByDateItem[];
}

export type ConsultProposalsGroupedByDateResponseDTO =
  ProposalGroupedByDateResponseDTO[];

export interface ProposalAnalysisItemDTO {
  item: string;
  horaAnalise: string;
  parecer: string;
  situacao: string;
  aprovador: string;
}

export interface ProposalAnalysisReasonDTO {
  item: string;
  proposta: number;
  situacao: string;
  label: string;
  motivorecusapadrao: string;
  aprovador: string;
  parecer: string;
  dataanalise: string;
  horaanalise: string;
}

export interface ConsultProposalSituationResponseDTO {
  lngProposta: string;
  strProduto: string;
  strDescrProduto: string;
  datDataSituacao: string;
  strReavaliacaoLimite: string;
  dblAtivRendaLiquida: number;
  dblLimiteCredito: number;
  dblLimiteCompras: number;
  dblLimiteSaques: number;
  dblLimiteParcelado: number;
  dblLimiteParceladoMensal: number;
  strSituacaoExterna: string;
  strDescricaoExterna: string;
  lstMotivosReprovacao: unknown[];
  lstItensAnalise: ProposalAnalysisItemDTO[];
}

export interface ConsultProposalResponseDTO {
  strSituacao: string;
  strSituacaoDescricao?: string;
  strInfoEmail?: string;
  datDataProposta: string;
  dblLimiteCredito: number;
  strResPais: string;
  dblLimiteCompras: number;
  strTipoProposta: ProposalTypeValue;
  strTipoPessoa: string;
  dblLimiteSaques: number;
  intDiaCartao: number;
  strResCep: string;
  strDigitador: string;
  strUsuarioEfetivacao: string;
  strResComplemento: string;
  strResEndereco: string;
  strResNumero: string;
  strResBairro: string;
  strAtivProfissao: string;
  datAtivDataAdmissao: string;
  dblComSalario: number;
  strEndTipoResidencia: string;
  strResCidade: string;
  strUF: string;
  strResUF: string;
  dblLimiteParcelado: number;
  dblLimiteParceladoMensal: number;
  datDataNascimento: string;
  strTrabalhaDesde: string;
  lstPontosAtendimento: string;
  dblLimite: number;
  dblLimiteMaximo: number;
  dblLimiteMinimo: number;
  dblRenda: number;
  intDiaVencimento: number;
  intFaseProposta: number;
  lngProposta: number;
  strBairro: string;
  strCelularDDD: string;
  strCelular: string;
  strCep: string;
  strCidade: string;
  strComplemento: string;
  strCpf: string;
  strEmail: string;
  strEndereco: string;
  strEstadoCivil: string;
  strNome: string;
  strNomeMae: string;
  strNomePai?: string;
  strNumero: string;
  strNumeroDocumento: string;
  strPrimeiroNome: string;
  strProduto: string;
  strBandeira: string;
  strProfissao: string;
  strSexo: string;
  strTelefoneAdicionalDDD: string;
  strTelefoneAdicional: string;
  strTipoCep: string | null;
  strTipoDocumento: string;
  strUf: string;
  imgSelfie: string | null;
  imgRgFrente: string | null;
  imgRgVerso: string | null;
  imgCnhFrente: string | null;
  imgCnhVerso: string | null;
  strProfissaoDescricao: string;
  imgAssinatura: string | null;
  datTrabDesde: string;
  strTelefoneOpcional: string | null;
  strNomeConjuge: string | null;
  strEnvioFatura: string;
  strOpcaoPagamento: string;
  strProdutoDescricao: string;
  strPontoAntendimentoNome: string;
  strProfissao1: string | null;
  strClasseProfissional: string;
  strConjuge: string;
  strNroDocIdent: string;
  strResTipoResidencia: string;
  strResMesAno: string;
  strRefPessNome1: string;
  strRefPessRel1: string;
  strRefPessFone1: string;
  strRefPessDdd1: string;
  strhabilitarPontoAtendimento: string;
  strAtendente: string;
  strPontoAtendimento: string;
  strPermiteAlteracao: string | null;
  strEfetivaPropostaViaToken: string;
  strTokenValidado: string;
  strFoneEnvioToken: string;
  strDDDEnvioToken: string;
  // Legacy fields for backward compatibility
  strCliente?: string;
  strReavaliacaoLimite?: string;
  // Secondary residential phone (CRTPROPOSTAS.RESDDD2/RESFONE2/RESTIPOFONE2)
  strResDDD?: string;
  strResFone?: string;
  strResTipoFone1?: string;
  strResDDD2?: string;
  strResFone2?: string;
  strResTipoFone2?: string;
  strPontoAtendimentoDescricao?: string;
  lstDocumentos?: unknown[];
  // Analysis items if included in response
  lstItensAnalise?: ProposalAnalysisItemDTO[];
}

export type GetProposalDetailsResponseDTO = unknown;

export type CreateProposalInputDTO = {
  intTransacao?: number;
  blnPreProposta?: boolean;
  datDataProposta?: string;
  datDataSituacao?: string;
  datNascimento?: string;
  dblLimiteCompras?: number;
  dblLimiteParcelado?: number;
  dblLimiteParceladoMensal?: number;
  dblLimiteCredito?: number;
  dblLimiteSaques?: number;
  dblScore?: number;
  intDiaCartao?: number;
  blnPreAprovado?: boolean;
  blnGerarPropostaAprovada?: boolean;
  blnOrigemApp?: boolean;
  intFaseProposta?: number;
  intMesa?: number;
  lngProposta?: number;
  strAtendente?: string;
  strDescrAtendente?: string;
  strAtivSetor?: string;
  strAtivDataAdmissao?: string;
  datAtivDataAdmissao?: string;
  datTrabDesde?: string;
  strEndTipoFone1?: string;
  strBandeira?: string;
  strCliente?: string; // CPF or CNPJ
  strConjuge?: string;
  strCodigoAutenticacao?: string;
  strCorrespondencia?: string;
  strDigitador?: string;
  strDescrDigitador?: string;
  strEmpresa?: string;
  // Backend documents "E" (EMAIL), "W" (APP), "T" (SMS), "C" (CORREIOS), but
  // legacy proposals also return values such as "M". Keep the field as a free
  // string so reanalysis can echo whatever ConsultarProposta returned.
  strEnvioFatura?: string;
  strEstCivil?: string;
  strFilial?: string;
  strHoraProposta?: string;
  strHoraSituacao?: string;
  strIdPush?: string;
  strInfoEmail?: string;
  strNome?: string;
  strNomeMae?: string;
  strOrigemProposta?: string;
  strOpcaoPagamentos?: string;
  strParecer?: string;
  strPontoAtendimento?: string;
  strPpe?: string;
  strProduto?: string;
  strResCep?: string;
  strReavaliacaoLimite?: string;
  strRenda?: number;
  strResDdd?: string;
  strResFone?: string;
  strResDDD2?: string;
  strResFone2?: string;
  strResTipoFone1?: string;
  strResTipoFone2?: string;
  strResEndereco?: string;
  strResNumero?: string;
  strResBairro?: string;
  strResCidade?: string;
  strResComplemento?: string;
  strAvalTipoFone1?: string;
  strResUF?: string;
  strSexo?: string;
  strSituacao?: PROPOSTA_SITUACAO;
  strTipoPessoa?: "F" | "J"; // F (FÍSICA), J (JURÍDICA)
  strTipoProposta?: ProposalTypeValue; // F (FIDELIDADE), A (AMBOS)
  strTelefone?: string;
  strDddTelefone?: string;
  strMensagemproposta?: string;
  strNroDocIdent?: string;
  strRefPessNome1?: string;
  strResPais?: string;
  strRefPessRel1?: string;
  strRefPessFone1?: string;
  strRefPessDdd1?: string;
  strEndMesAno?: string;
  strEndTipoResidencia?: string;
  strClasseProfissional?: string;
  strUsuarioAtivo?: string;
  intCartoesAdicionais?: number;
  strNomeAdicional1?: string;
  strCpfAdicional1?: string;
  strParentescoAdicional1?: string;
  strSexoAdicional1?: string;
  strDataNascimentoAdicional1?: string;
  datDataNascimentoAdicional1?: string;
  strNomeAdicional2?: string;
  strCpfAdicional2?: string;
  strParentescoAdicional2?: string;
  strSexoAdicional2?: string;
  strDataNascimentoAdicional2?: string;
  datDataNascimentoAdicional2?: string;
  strNomeAdicional3?: string;
  strCpfAdicional3?: string;
  strParentescoAdicional3?: string;
  strSexoAdicional3?: string;
  strDataNascimentoAdicional3?: string;
  datDataNascimentoAdicional3?: string;
  intDependentes?: number;
  strCidadeNascimento?: string;
  strUfNascimento?: string;
  strNacionalidade?: string;
  strUfEmiDocIdent?: string;
  strOrgaoEmiDocIdent?: string;
  strDataEmiDocIdent?: string;
  datDataEmiDocIdent?: string;
  strGrauInstrucao?: string;
  strResRamal1?: string;
  strEndDdd2?: string;
  strResRamal2?: string;
  strEndFone2?: string;
  strEndTipoFone2?: string;
  strEndDdd3?: string;
  strResRamal3?: string;
  strEndFone3?: string;
  strEndTipoFone3?: string;
  strResNomeRecado?: string;
  strTipoCorrespondencia?: TIPO_CORRESPONDENCIA;
  strResResideCom?: string;
  strAposentado?: string;
  strAtivEmpresa?: string;
  strAtivProfissao?: string;
  strAtivCargo?: string;
  dblComSalario?: number;
  dblRenda?: number;
  dblComOutrasRendas?: number;
  strComOutrasRendasOrigem?: string;
  intDiaRecebimento?: number;
  strComEmpresario?: string;
  strComEmpresaCnpj?: string;
  dblComEmpresaParticipacao?: number;
  strComEmpresaRamo?: string;
  strComCep?: string;
  strComEndereco?: string;
  strComNumero?: string;
  strComComplemento?: string;
  strComCidade?: string;
  strComBairro?: string;
  strComUf?: string;
  strComDDD?: string;
  strComTelefone?: string;
  strComRamal?: string;
  strConjugeCPF?: string;
  strConjugeNascimento?: string;
  strConjugeEmpresa?: string;
  strConjugeAtividade?: string;
  strConjugeCargo?: string;
  dblConjugeRenda?: number;
  strConjugeAdmissao?: string;
  strConjugeCep?: string;
  strConjugeEndereco?: string;
  strConjugeCidade?: string;
  strConjugeBairro?: string;
  strConjugeUf?: string;
  strConjugeDdd?: string;
  strConjugeFone?: string;
  strConjugeRamal?: string;
  strConjugeMae?: string;
  dblConjugeOutrasRendas?: number;
  strRefPessNome2?: string;
  strRefPessRel2?: string;
  strRefPessFone2?: string;
  strRefPessDdd2?: string;
  strRefPessNome3?: string;
  strRefPessRel3?: string;
  strRefPessFone3?: string;
  strRefPessDdd3?: string;
  strRefBanco1?: string;
  strRefTipoConta1?: string;
  strRefTempoConta1?: string;
  strRefAgencia1?: string;
  strRefConta1?: string;
  strRefContaDigito1?: string;
  strRefCidade1?: string;
  strRefUf1?: string;
  strRefBanco2?: string;
  strRefTipoConta2?: string;
  strRefTempoConta2?: string;
  strRefAgencia2?: string;
  strRefConta2?: string;
  strRefContaDigito2?: string;
  strRefCidade2?: string;
  strRefUf2?: string;
  strRefCartao1?: string;
  strRefTipoCartao1?: string;
  strRefCartao2?: string;
  strRefTipoCartao2?: string;
  strRefComercial2?: string;
  strRefCidadeComercial2?: string;
  strRefComercial3?: string;
  strRefCidadeComercial3?: string;
  strAltEndereco?: string;
  strAltNumero?: string;
  strAltComplemento?: string;
  strAltBairro?: string;
  strAltCidade?: string;
  strAltCep?: string;
  strAltUf?: string;
  dblPrestacaoCasaPropria?: number;
  dblAluguelMensal?: number;
  dblAreaCultivada?: number;
  strOrgaoClasse?: string;
  strRegistroOrgao?: string;
  intOrigemBeneficio?: number;
  strTipoBeneficio?: string;
  strNumeroBeneficio?: string;
  strVigenciaBeneficio?: string;
  intPpeTipoRelacionamento?: number;
  strPpeNome?: string;
  strPpeCpf?: string;
  strPpeFuncaoDataInicio?: string;
  datPpeFuncaoDataInicio?: string;
  intPpeFuncaoTipo?: number;
  dblValorCompra?: number;
  dblValorEncargos?: number;
  intPrazo?: number;
  strPlano?: string;
  dblValorPrestacao?: number;
  strTipoOperacao?: string;
  strPrimeiroVcto?: string;
  strConveniada?: string;
  strMatricula?: string;
  strConveniadaOrgao?: string;
  strMotivoAposentadoria?: string;
  strCausaAposentadoria?: string;
  strLiberacaoForma?: string;
  strLiberacaoBanco?: string;
  strLiberacaoAgencia?: string;
  strLiberacaoAgenciaDigito?: string;
  strLiberacaoConta?: string;
  strLiberacaoContaDigito?: string;
  strLiberacaoTipoConta?: string;
  strAuxReavaliacao?: string;
  strUsuario?: string;
  strNomePai?: string;
  strCpfCliente?: string;
  strCodImovelAuxiliadora?: string;
  strAuxiliadoraEndereco?: string;
  strAuxiliadoraCep?: string;
  strAuxiliadoraNumero?: string;
  strAuxiliadoraComplemento?: string;
  strAuxiliadoraBairro?: string;
  strAuxiliadoraCidade?: string;
  strAuxiliadoraUf?: string;
  strObservacoes?: string;
  strEstadoCivil?: string;
  strDataNasc?: string;
  datDataNasc?: string;
  strEndereco?: string;
  strEndComplemento?: string;
  strEndCidade?: string;
  strEndNumero?: string;
  strEndBairro?: string;
  strEndUf?: string;
  strEndDdd1?: string;
  strEndFone1?: string;
  strEmail?: string;
  dblAtivSalario?: number;
  dblOutrasRendasValor?: number;
  strOutrasRendasDescricao?: string;
  intAtivDiaRecebimento?: number;
  strSocioProprietario?: string;
  strEmpresaCgc?: string;
  intEmpresaParticipacao?: number;
  strEmpresaRamoAtividade?: string;
  strAtivCep?: string;
  strAtivEndereco?: string;
  strAtivEnderecoNumero?: string;
  strAtivEnderecoComplemento?: string;
  strAtivCidade?: string;
  strAtivBairro?: string;
  strAtivUf?: string;
  strAtivDdd?: string;
  strAtivFone?: string;
  strAtivRamal?: string;
  strConjugeNome?: string;
  strRefComercial1?: string;
  strRefCidadeComercial1?: string;
  datVigenciaBeneficio?: string;
  dblValorEncargo?: number;
  strVeiculosDescricao1?: string;
  strVeiculosAno1?: string;
  strVeiculosPlaca1?: string;
  strVeiculosRenavam1?: string;
  strVeiculosDescricao2?: string;
  strVeiculosAno2?: string;
  strVeiculosPlaca2?: string;
  strVeiculosRenavam2?: string;
  strHaveraPrimeiraCompraSemCartao?: string;
  blnGravaLogConferenciaDocumentos?: boolean;
  strProdutoPrimeiraCompraSemCartao?: string;
  lngPrazoPrimeiraCompraSemCartao?: number;
  dblValorPrimeiraCompraSemCartao?: number;
  strConceitoExterno?: string;
  intPacoteBeneficio?: number;
  strProdutoPre?: string;
  datValidadePre?: string;
  strTipoClientePre?: string;
  strRiscoPre?: string;
  dblLimitePre?: number;
  lstPortadorPJ?: LstPortadorPJ[];
  lngPropostaOriginal?: number;
  blnRetornoUnificado?: boolean;
  lstDocumentosID?: LstDocumentosID[];
  lstProfissionalLiberal?: LstProfissionalLiberal[];
  lstAgricultor?: LstAgricultor[];
  strTipoDocIdent?: string;
  strPossuiTelefoneDuplicado?: StrPossuiTelefoneDuplicado;
  strProdutoCsu?: string;
  strCadastroConferido?: string;
  intTelefonesConferidos?: number;
  strDestinoEmbossamento?: string;
  dblSaldoExterno?: number;
  dblSaldoExternoSaques?: number;
  dblSaldoExternoParcelado?: number;
  dblSaldoExternoParcMensal?: number;
  dblSaldoExternoAndamento?: number;
  dblSaldoExternoSaquesAndamento?: number;
  dblSaldoExternoParceladoAndamento?: number;
  dblSaldoExternoParcMensalAndamento?: number;
  strNomeUsuarioEfetivacao?: string;
  strCodigoExternoCliente?: string;
  strTitularidadeAdicional1?: string;
  lstVariaveis?: LstVariavei[];
  dblAtivFaturamentoAnual?: number;
  intEmpregos?: number;
  intMesaProposta?: number;
  lngNumeroCartao?: number;
  strAtivAnos?: string;
  strAtivDataConstituicao?: string;
  datAtivDataConstituicao?: string;
  strAtivEndPais?: string;
  strAtivFormaConstituicao?: string;
  strAtivMeses?: string;
  strAtivNomeFantasia?: string;
  strAtivPais?: string;
  strAtivPorte?: string;
  strBase64Biometria?: string;
  datBase64DataHora?: string;
  datDataHora?: string;
  strCgcCpfConferido?: string;
  strCodigoExterno?: string;
  strCodigoUsuarioExterno?: string;
  strConjugeClasseProfissional?: string;
  strConjugeRendaComprovada?: string;
  strDataRelacionamento?: string;
  datDataRelacionamento?: string;
  strEmpregoConferido?: string;
  strEnderecoConferido?: string;
  strEndFax?: string;
  strEndPais?: string;
  strFuncionario?: string;
  strIDOrigem?: string;
  strMatriculaVendedor?: string;
  strNomeDocumento?: string;
  strNotaComportamento?: string;
  strPatrimonio?: string;
  strRefCartaoAnos?: string;
  strRefCartaoMeses?: string;
  strSenhaWeb?: string;
  strTipoRelacionamento?: string;
  strUsuarioAtendente?: string;
  strUsuarioEfetivacao?: string;
  strToken?: string;
  strAtendenteExterno?: string;
  strArquivoBiometria?: string;
  strNomeArquivoBiometria?: string;
  strHashDocumento?: string;
  strFormaPagamentoMaquina?: string;
  dblAtivRendaLiquida?: number;
  strAtivFantasia?: string;
  strConjugeNaturalidade?: string;
  strConjugeNacionalidade?: string;
  strNomeReduzidoAdicional1?: string;
  strEstadoCivilAdicional1?: string;
  strDebitoConta?: string;
  strDebitoBanco?: string;
  strDebitoAgencia?: string;
  strDebitoAgenciaDigito?: string;
  strDebitoContaCorrente?: string;
  strDebitoContaDigito?: string;
  strDebitoTipoConta?: string;
  datDataCadastroOrigem?: string;
  strNomePOS?: string;
  strCosif?: string;
  strOperadora?: string;
  strAntecipacao?: string;
  strContribuinte?: string;
  strInscricaoEstadualContribuinte?: string;
  strBancoContribuinte?: string;
  strAgenciaContribuinte?: string;
  strDigitoAgenciaContribuinte?: string;
  strContaContribuinte?: string;
  strOperacaoCaixaContribuinte?: string;
  strContaPoupancaContribuinte?: string;
  intDiasAntecipacao?: number;
  dblTaxaAntecipacao?: number;
  dblTaxaDebito?: number;
  dblTaxaCredito?: number;
  dblTaxaParcelamento2a6?: number;
  dblTaxaParcelamento7a12?: number;
  strAutorizaSCR?: string;
  strPaisNacionalidade?: string;
  strCodCidade?: string;
  strAltCodCidade?: string;
  strSegmentoCnae?: string;
  strCodCnae?: string;
  strCodCnaeSecundario?: string;
  strCodCnaeSecundario1?: string;
  strCodCnaeSecundario2?: string;
  strCodCnaeSecundario3?: string;
  strCodCnaeSecundario4?: string;
  strCodCnaeSecundario5?: string;
  strIdPagamentoMaquina?: string;
  strIdTransacaoMaquina?: string;
  intQtdMaquinas?: number;
  dblValorBoletoProposta?: number;
  strModeloMaquina?: string;
  strCodGrupoClientesExterno?: string;
  strNaturezaJuridica?: string;
  strNire?: string;
  strSituacaoReceita?: string;
  strRamoAtividade?: string;
  strSegmentoProposta?: string;
  dblCapitalSocial?: number;
  intFuncionarios?: number;
  strInscricaoEstadual?: string;
  strFiliais?: string;
  strNomeReduzido?: string;
  dblLimiteComprasAtual?: number;
  dblLimiteSaquesAtual?: number;
  dblLimiteParceladoAtual?: number;
  dblLimiteParceladoMensalAtual?: number;
  strContaExterna?: string;
  strSeguro1?: string;
  strSeguro2?: string;
  strConsorcio1?: string;
  strConsorcio2?: string;
  dblVeiculosValor1?: number;
  dblVeiculosOnus1?: number;
  dblVeiculosValor2?: number;
  dblVeiculosOnus2?: number;
  dblImoveisOnus2?: number;
  strImoveisCidade2?: string;
  dblImoveisValor2?: number;
  strImoveisDescricao2?: string;
  strImoveisUf1?: string;
  strImoveisCidade1?: string;
  dblImoveisOnus1?: number;
  dblImoveisValor1?: number;
  strImoveisDescricao1?: string;
  strSituacaoAtendente?: string;
  strOperacaoVinculada?: string;
  strDocBiometria?: string;
  strDataProposta?: string;
  strImoveisUf2?: string;
  strLiberacaoBancoOp?: string;
  strLiberacaoAgenciaOp?: string;
  strLiberacaoContaCorrente?: string;
  strOrigemValidaEmail?: string;
  strOrgaoEmissor?: string;
  strRendaComprovada?: "S" | "N";
  strLongitude?: string;
  strLatitude?: string;
  strIdProcessoUnico?: string;
  lstGarantias?: LstGarantia[];
  strTokenValidado?: string;
  strFoneEnvioToken?: string;
  strDDDEnvioToken?: string;
  blnManterPendente?: boolean;
};

export type CreatePreProposalInputDTO = CreateProposalInputDTO;

export interface ProposalDocumentDetailDTO {
  strTipoDocumento: string;
  lngDocumento: string;
  strPermiteAlteracao: string;
  strObservacoes: string;
  thumbnail: string;
}

export type ProposalDetailsResponseDTO = {
  strNome: string;
  strProduto: string;
  strProdutoDescricao: string;
  datDataProposta: string;
  strPontoAtendimentoDescricao: string;
  strCpf: string;
  strCliente: string;
  lngProposta: number;
  strTipoProposta: ProposalTypeValue;
  strSituacao: string;
  strReavaliacaoLimite: string;
  strTokenValidado: string;
  strFoneEnvioToken: string;
  strDDDEnvioToken: string;
  lstDocumentos: ProposalDocumentDetailDTO[];
};

export type UpdateProposalInputDTO = Omit<
  CreateProposalInputDTO,
  "lngProposta" | "strUfEmiDocIdent"
> & {
  lngProposta: string;
};

export type UpdatePreProposalInputDTO = Omit<
  CreatePreProposalInputDTO,
  "lngProposta"
> & {
  lngProposta: string;
};

interface LstGarantia {
  lngProposta?: number;
  strProduto?: string;
  strTipoPessoa?: "F" | "J";
  strTipoGarantia?: string;
  intIdGarantia?: number;
  intClassificacaoGarantia?: number;
  intModalidadeGarantia?: number;
  strVeiculoTipo?: string;
  strVeiculoMarca?: string;
  strVeiculoDescricao?: string;
  strVeiculoCodigo?: string;
  strVeiculoAno?: string;
  strVeiculoModelo?: string;
  strVeiculoRenavam?: string;
  strVeiculoPlaca?: string;
  strVeiculoUfPlaca?: string;
  strVeiculoUfLicenciamento?: string;
  strVeiculoDataAvaliacao?: string;
  dblVeiculoValorAvaliacao?: number;
  strVeiculoChassi?: string;
  strVeiculoCor?: string;
  intVeiculoCombustivel?: number;
  strAvalTipoPessoa?: string;
  strAvalCpf?: string;
  strAvalNome?: string;
  strAvalConjuge?: string;
  strAvalNomeConjuge?: string;
  strAvalCep?: string;
  strAvalEndereco?: string;
  strAvalNumero?: string;
  strAvalComplemento?: string;
  strAvalBairro?: string;
  strAvalCidade?: string;
  strAvalUf?: string;
  strAvalEmail?: string;
  strAvalDdd1?: string;
  strAvalTelefone1?: string;
  strAvalTipoFone1?: string;
  strAvalNascimento?: string;
  strAvalNomeMae?: string;
  dblAvalRenda?: number;
  intAvalClasseProfissional?: number;
  strAvalEstadoCivil?: string;
  strAvalCnpjEmpresa?: string;
  strAvalDdd2?: string;
  strAvalTelefone2?: string;
  strAvalTipoFone2?: string;
  strAvalNomeEmpresa?: string;
  datAvalDataAdmissao?: string;
  strAvalPep?: string;
  strAvalConstaPep?: string;
  strAvalConstaPepparentes?: string;
  strAvalConstaRelacionados?: string;
  strAvalConstaOfac?: string;
  strAvalConstaGafi?: string;
  strAvalConstaIndisponibilidade?: string;
  strAvalRefBanco?: string;
  strAvalRefAgencia?: string;
  strAvalRefConta?: string;
  datAvalRefDataAbertura?: string;
  strImovelTipo?: string;
  strImovelTipoImplantacao?: string;
  strImovelNumeroCartorio?: string;
  strImovelNumeroRegistro?: string;
  strImovelDataRegistro?: string;
  strImovelNumeroMatricula?: string;
  strImovelDataAvaliacao?: string;
  dblImovelValorAvaliacao?: number;
  dblImovelAreaTotal?: number;
  dblImovelAreaPrivativa?: number;
  dblImovelAreaTerreno?: number;
  dblImovelAreaTestada?: number;
  dblImovelDormitorios?: number;
  dblImovelGaragens?: number;
  strImovelCep?: string;
  strImovelEndereco?: string;
  strImovelNumero?: string;
  strImovelComplemento?: string;
  strImovelCidade?: string;
  strImovelBairro?: string;
  strImovelUf?: string;
  strOutrosBensDescricao?: string;
  dblOutrosBensValor?: number;
}

interface LstVariavei {
  strOrigem?: string;
  strVariavel?: string;
  strValor?: string;
}

interface StrPossuiTelefoneDuplicado {}

interface LstAgricultor {
  strTipoProducao?: string;
  dblProducaoMensal?: number;
  strUnidade?: string;
  strLocalEntrega?: string;
  strDdd?: string;
  strTelefone?: string;
}

interface LstProfissionalLiberal {
  strReferencia?: string;
  strRefDDD?: string;
  strRefFone?: string;
}

interface LstDocumentosID {
  strID?: string;
  strTipoDocumento?: string;
  datDocumento?: string;
  lngProposta?: number;
  strDescrAtendente?: string;
  strAtendente?: string;
  strBandeira?: string;
  strIP?: string;
  strTokenAutenticacao?: string;
  strIdDispositivo?: string;
  strLongitude?: string;
  strLatitude?: string;
  lstOnboarding?: LstOnboarding[];
}

interface LstOnboarding {
  strTipo?: string;
  datData?: string;
  strHora?: string;
  strLongitude?: string;
  strLatitude?: string;
  strHash?: string;
  strBase64TermoAdesao?: string;
  strBase64Assinatura?: string;
  strVersaoDocumento?: string;
}

interface LstPortadorPJ {
  portador?: number;
  relacao?: string;
  cpf?: string;
  nome?: string;
  validade?: string;
  valorLimite?: number;
  limiteMaximo?: number;
  nascimento?: string;
  sexo?: string;
}

export type UploadDocumentInputDTO = {
  atributos?: Record<string, string>;
  strExtensao?: string;
  strTipoDocumento?: string;
  strObservacoes?: string;
  strBase64?: string;
  strNomeArquivo?: string;
  datDocumentoExterno?: string;
  strVisualizarWEB?: string;
  strCliente?: string;
  strCpfPortador?: string;
  strAutorizacao?: string;
  lngProposta?: string;
};

export type UploadDocumentOutputDTO = unknown;

export interface ProposalDocumentDTO {
  strDescrAtributo: string;
  strTipoDocumento: string;
  strDescrTipoDocumento: string;
  lngDocumento: string;
  strObservacoes: string;
  strDataDocumento: string;
  strHoraDocumento: string;
  thumbnail: string;
  strExtensao: string;
  strOutraProposta: string;
  strUsuario: string;
  strPropostaDocumento: string;
  strProposta: string;
  strClienteErAtivo: string;
  strFisicoSituacao: string;
  strDataComprovacao: string;
  strCodigoExterno: string;
  strBase64: string;
}

export interface ProposalDocumentsListResponseDTO {
  documentos: ProposalDocumentDTO[];
}

export type FetchPointsInputDTO = {
  strProduto: string;
  strBandeira: string;
};

export type FetchPointsResponseDTO = {
  ponto: string;
  descricao: string;
  permitePropostaSemPac: "S" | "N";
}[];

export interface LastCaptureResponseDTO {
  proposta: string;
  tipodefluxo: string;
  dataconsulta: string;
  horaconsulta: string;
  processoid: string;
}

export interface BiometryLastProcessResultResponseDTO {
  processoid: string | null;
}
